import React, { useRef, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import bgDesign from "../../../../assets/images/bgDesign.svg";
import awnLogo from "../../../../assets/images/awnLogo.svg";
import passwordIcon from "../../../../assets/images/passwordLock.svg";
import passwordEye from "../../../../assets/images/password.svg";
import "../../changePassword/changePassword.css";
import { useNavigate } from "react-router-dom";
import { allRoutes } from "../../../../assets/routes/allRoutes";
import * as Storage from "../../../../store/LocalStorage";
import DeleteConfirmationModal from "../table/DeleteModal";
import Tick from "../../../../assets/customIcons/Tick";
import { useTranslation } from "react-i18next";

// Custom Error Message Component
const CustomErrorMessage = ({ name, errors, touched }) => {
  return touched[name] && errors[name] ? (
    <div className="text-danger">
      <p>{errors[name]}</p>
    </div>
  ) : null;
};

const ChangePassword = () => {
  const { t, i18n } = useTranslation();

  const { changePassword } = MainServices();
  const route = allRoutes;
  const navigate = useNavigate();
  const firstInputRef = useRef(null);
  const [isCurrentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [errorMessageOnSubmit, setErrorMessageOnSubmit] = useState("");

  // Success modal states
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmTitleData, setConfirmTitleData] = useState({});
  const [confirmMsgData, setConfirmMsgData] = useState({});

  // Password validation schema using Yup
  const passwordValidationSchema = Yup.object().shape({
    currentPassword: Yup.string()
      .required("Current Password is required")
      .min(8, "Password must be at least 8 characters")
      .max(64, "Password must not exceed 64 characters"),

    newPassword: Yup.string()
      .required("New Password is required")
      .min(8, "Password must be at least 8 characters")
      .max(64, "Password must not exceed 64 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\W_]{8,}$/,
        "Password must include uppercase, lowercase, number, and special character"
      )
      .notOneOf(
        [Yup.ref("currentPassword")],
        "New password must be different from current password"
      ),

    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("newPassword")], "Passwords must match"),
  });

  const togglePasswordVisibility = (field) => {
    if (field === "current") {
      setCurrentPasswordVisible(!isCurrentPasswordVisible);
    } else if (field === "new") {
      setNewPasswordVisible(!isNewPasswordVisible);
    } else if (field === "confirm") {
      setConfirmPasswordVisible(!isConfirmPasswordVisible);
    }
  };

  const handleLogout = () => {
    Storage.clear();
    // navigate(allRoutes?.login);
    window.location.reload();
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    handleLogout();
  };

  const handlePasswordChange = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmNewPassword: values.confirmPassword,
      });

      if (response.data) {
        // Instead of logging out directly, show success modal
        setConfirmTitle("Password Changed Successfully!");
        setConfirmMsg(
          "Your password has been updated. Please log in again with your new password."
        );
        setSuccessModalOpen(true);
        setErrorMessageOnSubmit("");
        resetForm();
      }
    } catch (error) {
      setErrorMessageOnSubmit(t("An unexpected error occurred"));
      notifyWarn("An unexpected error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <img className="bg-design" src={bgDesign} alt="background design" />
      <div className="change-password-section z-10 py-4">
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={passwordValidationSchema}
          onSubmit={handlePasswordChange}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form className="create-password-container bg-white p-4 z-10 rounded-sm">
              <div className="verify-logo-container">
                <img className="awn-logo" src={awnLogo} alt="wosol logo" />
              </div>
              <div className="verify-inputs-container">
                <div className="verify-header">
                  <p className="verify-title">Set New Password</p>
                  <p className="verify-des mt-1">
                    Please enter your current password and new password to
                    update your account.
                  </p>
                </div>
                <div className="password-inputs">
                  {/* Current Password */}
                  <div className="form-group">
                    <div className="input-section">
                      <div className="label-container">
                        <label
                          htmlFor="currentPassword"
                          className="awn-input-label"
                        >
                          Current Password
                        </label>
                      </div>
                      <div className="input-icon-wrap">
                        <input
                          ref={firstInputRef}
                          name="currentPassword"
                          id="currentPassword"
                          type={isCurrentPasswordVisible ? "text" : "password"}
                          className="password-input"
                          placeholder="Enter Your Current Password"
                          value={values.currentPassword}
                          onBlur={handleBlur}
                          onChange={(e) =>
                            setFieldValue("currentPassword", e.target.value)
                          }
                        />
                        <img
                          className="input-icon"
                          src={passwordIcon}
                          alt="password lock icon"
                        />
                        <span
                          className={`password-icon ${
                            isCurrentPasswordVisible ? "" : "crossed-eye"
                          }`}
                          onClick={() => togglePasswordVisibility("current")}
                        >
                          <img
                            className=""
                            src={passwordEye}
                            alt={
                              isCurrentPasswordVisible
                                ? "Hide password"
                                : "Show password"
                            }
                          />
                        </span>
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="currentPassword"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* New Password */}
                  <div className="form-group">
                    <div className="input-section">
                      <div className="label-container">
                        <label
                          htmlFor="newPassword"
                          className="awn-input-label"
                        >
                          New Password
                        </label>
                      </div>
                      <div className="input-icon-wrap">
                        <input
                          name="newPassword"
                          id="newPassword"
                          type={isNewPasswordVisible ? "text" : "password"}
                          className="password-input"
                          placeholder="Enter Your New Password"
                          value={values.newPassword}
                          onBlur={handleBlur}
                          onChange={(e) =>
                            setFieldValue("newPassword", e.target.value)
                          }
                        />
                        <img
                          className="input-icon"
                          src={passwordIcon}
                          alt="password lock icon"
                        />
                        <span
                          className={`password-icon ${
                            isNewPasswordVisible ? "" : "crossed-eye"
                          }`}
                          onClick={() => togglePasswordVisibility("new")}
                        >
                          <img
                            className=""
                            src={passwordEye}
                            alt={
                              isNewPasswordVisible
                                ? "Hide password"
                                : "Show password"
                            }
                          />
                        </span>
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="newPassword"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* Confirm Password */}
                  <div className="form-group">
                    <div className="input-section">
                      <div className="label-container">
                        <label
                          htmlFor="confirmPassword"
                          className="awn-input-label"
                        >
                          Confirm Password
                        </label>
                      </div>
                      <div className="input-icon-wrap">
                        <input
                          name="confirmPassword"
                          id="confirmPassword"
                          type={isConfirmPasswordVisible ? "text" : "password"}
                          className="password-input"
                          placeholder="Confirm Your New Password"
                          value={values.confirmPassword}
                          onBlur={handleBlur}
                          onChange={(e) =>
                            setFieldValue("confirmPassword", e.target.value)
                          }
                          onPaste={(e) => {
                            e.preventDefault();
                            return false;
                          }}
                        />
                        <img
                          className="input-icon"
                          src={passwordIcon}
                          alt="password lock icon"
                        />
                        <span
                          className={`password-icon ${
                            isConfirmPasswordVisible ? "" : "crossed-eye"
                          }`}
                          onClick={() => togglePasswordVisibility("confirm")}
                        >
                          <img
                            className=""
                            src={passwordEye}
                            alt={
                              isConfirmPasswordVisible
                                ? "Hide password"
                                : "Show password"
                            }
                          />
                        </span>
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="confirmPassword"
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>
                <div className="update-btn-container">
                  <button
                    className="update-btn"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Save & Update"}
                  </button>
                </div>
              </div>

              {errorMessageOnSubmit && (
                <div className="text-danger">
                  <p>{errorMessageOnSubmit}</p>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>

      {/* Success Modal */}
      {successModalOpen && (
        <DeleteConfirmationModal
          icon={<Tick />}
          isOpen={successModalOpen}
          headline={confirmTitle}
          headlineData={confirmTitleData}
          paraText={confirmMsg}
          paraTextData={confirmMsgData}
          confirmText="Go To Login"
          isConfirm={true}
          onConfirm={handleSuccessModalClose}
          onCancel={handleSuccessModalClose}
          onClose={handleSuccessModalClose}
        />
      )}
    </>
  );
};

export default ChangePassword;
